import React, { useRef, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { H2, H3, Text } from '@amzn/stencil-react-components/text'
import {
    IconAlertCircleFillSmall,
    IconBin,
    IconDocument,
    IconUpload,
} from '@amzn/stencil-react-icons'

import {
    mustSkipContainerCreation,
    ParsedSpreadsheet,
    pluralize,
    SpreadsheetValidation,
} from 'src/pages/import-content/ImportContentModel'

export interface UploadStepProps {
    isLoadingModule: boolean
    loadError?: string
    moduleName?: string
    file?: File
    placeholders: string[]
    spreadsheet?: ParsedSpreadsheet
    validation?: SpreadsheetValidation
    onFileAttached: (file?: File) => void
    onDownloadSample: () => void
}

const ColumnChip = ({ name, backgroundColor }: { name: string; backgroundColor: string }) => (
    <View
        backgroundColor={backgroundColor}
        borderRadius={4}
        padding={{ top: 'S100', bottom: 'S100', left: 'S300', right: 'S300' }}
    >
        <Text fontSize='T200' color='neutral90'>
            {name}
        </Text>
    </View>
)

const MissingColumns = ({ missing }: { missing: string[] }) => (
    <Col gridGap='S300' dataTestId='csv-missing-columns'>
        <Text fontSize='T200' color='neutral90'>
            Missing ({missing.length})
        </Text>
        <Row gridGap='S300' flexWrap='wrap'>
            {missing.map((name) => (
                <ColumnChip key={name} name={name} backgroundColor='red05' />
            ))}
        </Row>
        <Col gridGap='S200'>
            {missing.map((name) => (
                <Row key={name} alignItems='center' gridGap='S200' flexWrap='wrap'>
                    <IconAlertCircleFillSmall title='Error' color='red70' />
                    <Text fontSize='T100' color='neutral70'>
                        Column
                    </Text>
                    <ColumnChip name={name} backgroundColor='neutral05' />
                    <Text fontSize='T100' color='neutral70'>
                        is missing from your file. The template expects it.
                    </Text>
                </Row>
            ))}
        </Col>
    </Col>
)

const SpreadsheetFileInput = ({
    file,
    onFileAttached,
}: {
    file?: File
    onFileAttached: (file?: File) => void
}) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const openFilePicker = () => (inputRef.current as HTMLInputElement).click()

    return (
        <>
            {file ? (
                <Row
                    alignItems='center'
                    gridGap='S300'
                    padding='S300'
                    dataTestId='attached-spreadsheet'
                    style={{ border: '1px solid #E1E4EA', borderRadius: '4px' }}
                >
                    <IconDocument title='' aria-hidden />
                    <Text fontSize='T200' style={{ flex: 1 }}>
                        {file.name}
                    </Text>
                    <Button
                        variant={ButtonVariant.Secondary}
                        icon={<IconUpload title='' aria-hidden />}
                        onClick={openFilePicker}
                        dataTestId='replace-file-button'
                    >
                        Replace file
                    </Button>
                    <Button
                        variant={ButtonVariant.Tertiary}
                        icon={<IconBin title='Delete file' color='red90' />}
                        onClick={() => onFileAttached(undefined)}
                        dataTestId='delete-file-button'
                    />
                </Row>
            ) : (
                <View
                    dataTestId='spreadsheet-drop-zone'
                    onDragOver={(event: React.DragEvent) => {
                        event.preventDefault()
                        setIsDragging(true)
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(event: React.DragEvent) => {
                        event.preventDefault()
                        setIsDragging(false)
                        onFileAttached(event.dataTransfer.files[0])
                    }}
                    style={{
                        border: `1px dashed ${isDragging ? '#0F5FD7' : '#8B96A3'}`,
                        borderRadius: '4px',
                        backgroundColor: isDragging ? '#E8F0FD' : '#F1F4F7',
                        minHeight: '350px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Col alignItems='center' gridGap='S200'>
                        <H3 fontSize='T300'>Drag and drop your spreadsheet here</H3>
                        <Text fontSize='T200'>.CSV files supported</Text>
                        <Text fontSize='T200'>or</Text>
                        <Button
                            variant={ButtonVariant.Primary}
                            icon={<IconUpload title='' aria-hidden />}
                            onClick={openFilePicker}
                            dataTestId='upload-file-button'
                        >
                            Upload file
                        </Button>
                    </Col>
                </View>
            )}
            <input
                ref={inputRef}
                id='import-content-file-upload'
                type='file'
                accept='.csv,text/csv'
                hidden
                onChange={(event) => {
                    onFileAttached(event.target.files?.[0])
                    event.target.value = ''
                }}
            />
        </>
    )
}

export const UploadStep = ({
    isLoadingModule,
    loadError,
    moduleName,
    file,
    placeholders,
    spreadsheet,
    validation,
    onFileAttached,
    onDownloadSample,
}: UploadStepProps) => (
    <Card isElevated width='100%' dataTestId='import-content-upload-step'>
        <Col gridGap='S300' width='100%'>
            <Row alignItems='center' gridGap='S300' width='100%'>
                <H2 fontSize='T300'>Upload your spreadsheet</H2>
                {isLoadingModule && <Spinner dataTestId='load-template-spinner' />}
            </Row>

            {loadError && (
                <MessageBanner type={MessageBannerType.Error} dataTestId='load-template-error'>
                    {loadError}
                </MessageBanner>
            )}

            {moduleName && (
                <Col gridGap='S200' dataTestId='template-module-info'>
                    <Text fontSize='T200' color='neutral70'>
                        Placeholders ({placeholders.length})
                    </Text>
                    <View
                        dataTestId='template-placeholders'
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
                    >
                        {placeholders.map((name) => (
                            <View
                                key={name}
                                backgroundColor='neutral05'
                                borderRadius={16}
                                padding={{
                                    top: 'S100',
                                    bottom: 'S100',
                                    left: 'S300',
                                    right: 'S300',
                                }}
                            >
                                <Text fontSize='T100' color='neutral90'>
                                    {name}
                                </Text>
                            </View>
                        ))}
                    </View>
                    <Row>
                        <Button
                            dataTestId='download-sample-csv'
                            variant={ButtonVariant.Tertiary}
                            onClick={onDownloadSample}
                        >
                            Download sample CSV
                        </Button>
                    </Row>
                </Col>
            )}

            {moduleName && (
                <Col gridGap='S300' width='100%'>
                    <SpreadsheetFileInput file={file} onFileAttached={onFileAttached} />
                    <Text fontSize='T100' color='neutral70'>
                        Columns must match the placeholders above — use “Download sample CSV” for
                        the exact headers. You move on to Review as soon as they do.
                    </Text>

                    {spreadsheet && (
                        <Col gridGap='S200' dataTestId='csv-validation'>
                            <Text fontSize='T200' color='neutral90'>
                                {pluralize(spreadsheet.rowCount, 'row')} →{' '}
                                {mustSkipContainerCreation(spreadsheet)
                                    ? 'the item pool, without Item Container modules'
                                    : pluralize(
                                          spreadsheet.containerCount,
                                          'Item Container module'
                                      )}
                            </Text>
                            {validation && validation.missing.length > 0 ? (
                                <MissingColumns missing={validation.missing} />
                            ) : (
                                <MessageBanner
                                    type={MessageBannerType.Success}
                                    isDismissible={false}
                                    dataTestId='csv-columns-ok'
                                >
                                    All placeholders have a matching column.
                                </MessageBanner>
                            )}
                            {validation && validation.extra.length > 0 && (
                                <MessageBanner
                                    type={MessageBannerType.Informational}
                                    isDismissible={false}
                                    dataTestId='csv-extra-columns'
                                >
                                    Extra column(s) not used by the template:{' '}
                                    {validation.extra.join(', ')}
                                </MessageBanner>
                            )}
                        </Col>
                    )}
                </Col>
            )}
        </Col>
    </Card>
)
